/**
 * MODULE: platform/types/layer.types
 * PURPOSE: Canonical canvas layer model for slide editing (slide canvas).
 * DEPENDENCIES: none (uses globalThis.crypto for ids)
 * PUBLIC_API: Layer, SlideLayer, TextLayer, ImageLayer, ShapeLayer,
 *   LayerContent, createDefaultLayer, LayerPatch, isTextLayer, isImageLayer, isShapeLayer
 * SIDE_EFFECTS: none
 * AGENT_SAFETY: 🟢 SAFE
 */

function newLayerId(): string {
  return globalThis.crypto.randomUUID();
}

// ─── Content variants ────────────────────────────────────────────────────────

export interface TextContent {
  kind: 'text';
  value: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: 400 | 500 | 600 | 700;
  fontStyle: 'normal' | 'italic';
  textDecoration: 'none' | 'underline';
  textAlign: 'left' | 'center' | 'right';
  color: string;
  lineHeight: number;
  letterSpacing: number;
}

export interface ImageContent {
  kind: 'image';
  src: string;
  alt: string;
  objectFit: 'cover' | 'contain' | 'fill';
  naturalWidth: number;
  naturalHeight: number;
  /** Horizontal flip (CSS scaleX -1). */
  flipH?: boolean;
  /** Vertical flip (CSS scaleY -1). */
  flipV?: boolean;
}

export interface ShapeContent {
  kind: 'shape';
  shape: 'rectangle' | 'ellipse' | 'triangle' | 'line';
  fill: string;
  stroke: string;
  strokeWidth: number;
  borderRadius: number;
}

export type LayerContent = TextContent | ImageContent | ShapeContent;

// ─── Layer style (visual overrides, separate from content) ───────────────────

export interface LayerStyle {
  opacity: number;
  shadow: string | null;
  border: string | null;
}

// ─── Core Layer type ──────────────────────────────────────────────────────────

export type LayerType = 'text' | 'image' | 'shape';

export interface Layer {
  id: string;
  type: LayerType;
  name: string;
  zIndex: number;
  locked: boolean;
  visible: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  style: LayerStyle;
  content: LayerContent;
}

// ─── Narrowed layer aliases & spec-name alias ─────────────────────────────────

// Layer is the canonical canvas element. SlideLayer is the spec-name alias
// (see Epic 3 / E3-1) — they are structurally identical. Prefer Layer in
// internal code; SlideLayer is provided so external task contracts and
// future renderers can reference the spec name without churn.
export type TextLayer = Layer & { type: 'text'; content: TextContent };
export type ImageLayer = Layer & { type: 'image'; content: ImageContent };
export type ShapeLayer = Layer & { type: 'shape'; content: ShapeContent };
export type SlideLayer = TextLayer | ImageLayer | ShapeLayer;

export function isTextLayer(layer: Layer): layer is TextLayer {
  return layer.type === 'text' && layer.content.kind === 'text';
}
export function isImageLayer(layer: Layer): layer is ImageLayer {
  return layer.type === 'image' && layer.content.kind === 'image';
}
export function isShapeLayer(layer: Layer): layer is ShapeLayer {
  return layer.type === 'shape' && layer.content.kind === 'shape';
}

// ─── Mutation helpers ─────────────────────────────────────────────────────────

export type LayerPatch = Partial<Omit<Layer, 'id' | 'type'>> & { id: string };

export function createDefaultLayer(type: LayerType, existingLayers: Layer[]): Layer {
  const index = existingLayers.filter((l) => l.type === type).length + 1;
  const baseContent: Record<LayerType, LayerContent> = {
    text: {
      kind: 'text',
      value: '',
      fontFamily: 'var(--font-body)',
      fontSize: 16,
      fontWeight: 400,
      fontStyle: 'normal',
      textDecoration: 'none',
      textAlign: 'left',
      color: 'var(--color-text)',
      lineHeight: 1.5,
      letterSpacing: 0,
    },
    image: {
      kind: 'image',
      src: '',
      alt: '',
      objectFit: 'cover',
      naturalWidth: 1920,
      naturalHeight: 1080,
      flipH: false,
      flipV: false,
    },
    shape: {
      kind: 'shape',
      shape: 'rectangle',
      fill: 'var(--color-surface-offset)',
      stroke: 'transparent',
      strokeWidth: 0,
      borderRadius: 8,
    },
  };
  return {
    id: newLayerId(),
    type,
    name: `${type.charAt(0).toUpperCase() + type.slice(1)} ${index}`,
    zIndex: existingLayers.length,
    locked: false,
    visible: true,
    x: 10,
    y: 10,
    width: 40,
    height: 20,
    rotation: 0,
    style: { opacity: 1, shadow: null, border: null },
    content: baseContent[type],
  };
}
